import axios from 'axios';
import camelizeKeys from 'camelcase-keys';
import QueryString from 'query-string';
import debounceActionCreators from 'helpers/redux/debounce_action_creators';
import RansackQuery from 'helpers/ransack_query';
import { get } from 'helpers/api';

import { searchTags } from 'components/tasks/tags_api';
import { sortTasks } from '../../utils';
import { map } from 'lodash';

const queryKeys = {
  assigneeId: 'q[assignee_id_eq]',
  callListId: 'q[task_lists_id_eq]',
  creatorId: 'q[creator_id_eq]',
  endDate: 'q[date_before]',
  includeUntagged: 'include_untagged',
  searchText: 'search_text',
  startDate: 'q[date_after]',
  taskType: 'q[task_type_eq]',
  tagIds: 'with_tags'
};

const debouncedActionCreators = debounceActionCreators({ retrieveTasks, getCallLists: getCallList }, 1000);

export const RETRIEVE_RECORDS_STARTED = 'RETRIEVE_RECORDS_STARTED';
export const retrieveRecordsStarted = () => ({ type: RETRIEVE_RECORDS_STARTED });

export const SET_CURRENT_CALL_LIST = 'SET_CURRENT_CALL_LIST';
export const setCurrentCallList = callList => ({ type: SET_CURRENT_CALL_LIST, callList });

export const SET_TASKS = 'SET_TASKS';
export const setTasks = tasks => ({ type: SET_TASKS, tasks });

export const SET_TASK_PAGE = 'SET_TASK_PAGE';
export const setTaskPage = (page, hasNextPage) => ({ type: SET_TASK_PAGE, hasNextPage, page });

export const SET_TAGS = 'SET_TAGS';
export const setTags = tags => ({ type: SET_TAGS, tags });

export function nextTaskPage() {
  return (dispatch, getState) => {
    const { paths, query } = getState();
    const path = paths.tasks;
    const params = getRansackQuery(query);
    params.page = getState().search.lastPageLoaded + 1;

    return axios.get(path, { params }).then((response) => {
      const existingTasks = getState().search.tasks;
      const newTasks = map(response.data.tasks, task => taskFromApi(task));
      dispatch(setTasks([...existingTasks, ...newTasks]));
      dispatch(setTaskPage(response.data.meta.page, response.data.meta.has_next_page));
    });
  };
}

export function retrieveTasks() {
  return (dispatch, getState) => {
    dispatch(setTasks([]));
    dispatch(retrieveRecordsStarted());
    const { paths, query } = getState();
    const path = paths.tasks;
    const params = getRansackQuery(query);
    return axios.get(path, { params }).then((response) => {
      const tasks = map(response.data.tasks, task => taskFromApi(task));
      const sortedTasks = sortTasks(tasks);
      dispatch(setTasks(sortedTasks));
      if (response.data.meta) {
        dispatch(setTaskPage(response.data.meta.page, response.data.meta.has_next_page));
      }
    });
  };
}

export function downloadTasksAsCsv() {
  return (_, getState) => {
    const { paths, query } = getState();
    const queryString = QueryString.stringify(getRansackQuery(query));
    window.location.assign(`${paths.tasksToCsv}?${queryString}`);
  };
}

export function delayRetrieveTasks() {
  return dispatch => debouncedActionCreators.retrieveTasks(dispatch);
}

function getRansackQuery(query) {
  const { status, ...filterOptions } = query;
  let ransack = new RansackQuery(filterOptions, queryKeys).toRansack();
  ransack = {
    ...ransack,
    ...getStatusQuery(status)
  };
  return ransack;
}

function getStatusQuery(status) {
  const query = {};

  if (status == null) {
    query[`q[open]`] = true;
  } else if (status !== 'all' && status !== 'project') {
    query[`q[${status}]`] = true;
  }

  return query;
}

export function getCallList() {
  return (dispatch, getState) => {
    const currentCallListId = getState().query.callListId;
    if (currentCallListId === null) {
      return;
    }

    dispatch(retrieveRecordsStarted());
    return get(
      `/contacts/call_lists/${currentCallListId}.json`,
      { params: { includeTasks: true } }
    ).then((response) => {
      const newCallList = response.callList;
      dispatch(setCurrentCallList(camelizeKeys(newCallList)));
      dispatch(retrieveTasks());
    });
  };
}

function taskFromApi(apiTask) {
  const { assignees, associatedModels, taskListItems, taskTaggedRecords, ...task } = camelizeKeys(apiTask);
  task.assignees = assignees ? camelizeKeys(assignees) : [];
  task.associatedModels = associatedModels ? camelizeKeys(associatedModels) : [];
  task.taskListItems = taskListItems ? camelizeKeys(taskListItems) : [];
  task.taskTaggedRecords = taskTaggedRecords ? camelizeKeys(taskTaggedRecords) : [];
  return task;
}

export const retrieveTags = (searchText = null)  => {
  return (dispatch) => {
    searchTags(searchText, (tags) => {
      const options = tags.map(tag => ({ value: tag.id.toString(), label: tag.name }));
      dispatch(setTags(options));
    });
  };
};
